import { computed, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { TransactionService } from "@modules/checkout/services/transaction/transaction.service";
import { TransactionStatus } from "@modules/checkout/enums/transaction-status";
import { PaymentAttempt } from "@modules/checkout/types/payment-attempt";
import { TransactionSchema } from "@modules/checkout/schemas/transaction.schema";
import { PaymentStatus } from "@modules/checkout/enums/payment-status";
import { PaymentMethod } from "@modules/checkout/enums/payment-method";

@Injectable()
export class TransactionStore {
  private readonly router = inject(Router);
  public readonly transactionId = computed(
    () =>
      this.router.routerState.snapshot.root.firstChild!.firstChild!.paramMap?.get("transaction") ??
      null,
  );

  private readonly service = inject(TransactionService);

  public readonly resource = this.service.getTransaction(this.transactionId);

  public readonly isTransactionPaid = computed(
    () =>
      this.resource.hasValue() &&
      this.resource.value().CurrentPaymentAttempt?.Status === PaymentStatus.Completed,
  );

  public readonly hasBoletoLink = computed(() => {
    if (!this.resource.hasValue()) return false;
    const payment = this.resource.value().CurrentPaymentAttempt;
    return payment?.Method === PaymentMethod.Boleto && !!payment?.ExtraData?.Link;
  });

  public readonly boletoLink = computed(() => {
    if (!this.resource.hasValue()) return null;
    const payment = this.resource.value().CurrentPaymentAttempt;
    return payment?.Method === PaymentMethod.Boleto ? (payment?.ExtraData?.Link ?? null) : null;
  });

  public readonly isBoletoPending = computed(() => {
    if (!this.resource.hasValue()) return null;
    const payment = this.resource.value().CurrentPaymentAttempt;
    return payment?.Method === PaymentMethod.Boleto && payment.Status === PaymentStatus.Pending;
  });

  public readonly isFraudAnalysisReview = computed(() => {
    if (!this.resource.hasValue()) return false;
    const payment = this.resource.value().CurrentPaymentAttempt;
    if (!payment || payment.Method !== PaymentMethod.CreditCard) return false;
    if (payment.Status !== PaymentStatus.Pending) return false;
    return payment.ExtraData?.FraudAnalysisReview === true;
  });

  public readonly isLoading = computed(
    () => this.resource.isLoading() && !this.resource.hasValue(),
  );

  public readonly error = computed(() => {
    if (this.resource.isLoading()) return false;
    if (this.resource.error()) return true;
    return (
      !this.resource.hasValue() || this.resource.value().Status !== TransactionStatus.Available
    );
  });

  public updatePayment(payment: PaymentAttempt) {
    this.resource.update((payload) =>
      TransactionSchema.parse({ ...payload, CurrentPaymentAttempt: payment }),
    );
  }
}
